import { ref, computed, onMounted, onUnmounted, readonly } from 'vue';
import { isPlatform, getPlatforms } from '@ionic/vue';
import { Capacitor } from '@capacitor/core';
import type { LayoutContext } from '@/layouts/types';

/**
 * Types de plateforme native
 */
export type NativePlatform = 'ios' | 'android' | 'web';

/**
 * Composable pour détecter le type d'appareil, la taille d'écran et le contexte d'utilisation
 *
 * Trois contextes d'utilisation :
 * - App Native (Capacitor) : isNativeApp=true, layoutContext='native-mobile'
 * - Navigateur Mobile : isMobileWeb=true, layoutContext='mobile-web'
 * - Navigateur Desktop : isDesktopWeb=true, layoutContext='desktop-web'
 */
export function useDeviceDetection() {
  // Diagnostic détaillé de la détection de plateforme (seulement en dev)
  // if (import.meta.env.DEV) {
  //   console.group('🔍 Diagnostic de détection de plateforme');
  //   console.log('📱 Plateformes Ionic:', getPlatforms());
  //   console.log('🌐 User Agent:', navigator.userAgent);
  //   console.log('📐 Dimensions écran:', window.innerWidth + 'x' + window.innerHeight);
  //   console.log('👆 Tactile supporté:', 'ontouchstart' in window);
  //   console.log('🖥️ DevicePixelRatio:', window.devicePixelRatio);
  //   console.log('⚡ Capacitor Native:', Capacitor.isNativePlatform());
  //   console.log('📲 Capacitor Platform:', Capacitor.getPlatform());
  //   console.groupEnd();
  // }

  const screenWidth = ref(window.innerWidth);
  const screenHeight = ref(window.innerHeight);

  // Seuils de détection mobile
  const MOBILE_WIDTH_THRESHOLD = 768;
  const TABLET_WIDTH_THRESHOLD = 1024;

  // ============================================
  // DÉTECTION CAPACITOR (prioritaire)
  // ============================================

  /**
   * Vérifie si l'app tourne dans un contexte natif Capacitor (iOS/Android)
   * C'est la méthode la plus fiable pour détecter une app native
   */
  const isNativeApp = computed(() => Capacitor.isNativePlatform());

  /**
   * Retourne la plateforme native : 'ios', 'android' ou 'web'
   */
  const nativePlatform = computed((): NativePlatform => {
    const platform = Capacitor.getPlatform();
    if (platform === 'ios') return 'ios';
    if (platform === 'android') return 'android';
    return 'web';
  });

  /**
   * Vérifie si c'est l'app native iOS
   */
  const isNativeIOS = computed(() => isNativeApp.value && nativePlatform.value === 'ios');

  /**
   * Vérifie si c'est l'app native Android
   */
  const isNativeAndroid = computed(() => isNativeApp.value && nativePlatform.value === 'android');

  // ============================================
  // DÉTECTION TAILLE D'ÉCRAN ET TACTILE
  // ============================================

  // Détection tactile
  const isTouchDevice = computed(
    () =>
      'ontouchstart' in window ||
      navigator.maxTouchPoints > 0 ||
      // @ts-expect-error msMaxTouchPoints n'existe pas dans tous les navigateurs
      navigator.msMaxTouchPoints > 0
  );

  // Détection basée sur la taille d'écran
  const isMobileScreen = computed(() => screenWidth.value < MOBILE_WIDTH_THRESHOLD);
  const isTabletScreen = computed(
    () => screenWidth.value >= MOBILE_WIDTH_THRESHOLD && screenWidth.value < TABLET_WIDTH_THRESHOLD
  );
  const isDesktopScreen = computed(() => screenWidth.value >= TABLET_WIDTH_THRESHOLD);

  // ============================================
  // DÉTECTION CONTEXTE WEB (navigateur)
  // ============================================

  /**
   * Vérifie si c'est un navigateur mobile (pas une app native)
   * Utilise User Agent + taille d'écran + tactile
   */
  const isMobileWeb = computed(() => {
    // Si c'est une app native, ce n'est pas du mobile web
    if (isNativeApp.value) return false;

    // Vérifie le User Agent pour mobile
    const mobileUserAgent = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
      navigator.userAgent
    );

    // Combine User Agent + taille d'écran mobile + tactile
    return mobileUserAgent || (isMobileScreen.value && isTouchDevice.value);
  });

  /**
   * Vérifie si c'est un navigateur desktop
   */
  const isDesktopWeb = computed(() => {
    // Si c'est une app native, ce n'est pas du desktop web
    if (isNativeApp.value) return false;

    // C'est desktop web si ce n'est pas mobile web
    return !isMobileWeb.value;
  });

  // ============================================
  // CONTEXTE DE LAYOUT (principal)
  // ============================================

  /**
   * Retourne le contexte de layout pour adapter l'UI
   * - 'native-mobile' : App Capacitor sur iOS/Android
   * - 'mobile-web' : Site web sur navigateur mobile
   * - 'desktop-web' : Site web sur navigateur desktop
   */
  const layoutContext = computed((): LayoutContext => {
    if (isNativeApp.value) return 'native-mobile';
    if (isMobileWeb.value) return 'mobile-web';
    return 'desktop-web';
  });

  // ============================================
  // ANCIENNES PROPRIÉTÉS (rétrocompatibilité)
  // ============================================

  // Détection basée sur la plateforme Ionic
  const isNativeMobile = computed(() => isPlatform('mobile'));
  const isNativeTablet = computed(() => isPlatform('tablet'));
  const isNativeDesktop = computed(() => isPlatform('desktop'));

  // Détection plus robuste basée sur plusieurs critères
  const isRealMobileDevice = computed(() => {
    const platforms = getPlatforms();
    // Vérifie si c'est un vrai appareil mobile (pas juste mobileweb)
    return (
      platforms.includes('android') ||
      platforms.includes('ios') ||
      platforms.includes('iphone') ||
      platforms.includes('ipad') ||
      (platforms.includes('mobile') && !platforms.includes('mobileweb'))
    );
  });

  // Détection améliorée basée sur l'environnement réel
  const isRealDesktop = computed(() => {
    // Plusieurs critères pour détecter un vrai desktop
    return (
      !isRealMobileDevice.value &&
      (isDesktopScreen.value ||
        (!isTouchDevice.value && screenWidth.value >= MOBILE_WIDTH_THRESHOLD))
    );
  });

  // Détection combinée (recommandée avec logique améliorée)
  const isMobile = computed(() => {
    // App native = toujours mobile
    if (isNativeApp.value) return true;
    // Priorité au vrai appareil mobile, sinon taille d'écran + tactile
    return (
      isRealMobileDevice.value ||
      (isMobileScreen.value && isTouchDevice.value && !isRealDesktop.value)
    );
  });

  const isTablet = computed(() => {
    // Détection tablet basée sur taille et pas mobile
    return (
      isNativeTablet.value || (isTabletScreen.value && !isMobile.value && !isRealDesktop.value)
    );
  });

  const isDesktop = computed(() => {
    // Desktop si pas mobile/tablet ET (grande taille OU pas tactile OU explicitement desktop)
    return (
      isRealDesktop.value ||
      (!isMobile.value && !isTablet.value && (isDesktopScreen.value || !isTouchDevice.value))
    );
  });

  // Détection d'orientation
  const isPortrait = computed(() => screenHeight.value > screenWidth.value);
  const isLandscape = computed(() => screenWidth.value > screenHeight.value);

  // Type d'appareil simplifié
  const deviceType = computed(() => {
    if (isMobile.value) return 'mobile';
    if (isTablet.value) return 'tablet';
    return 'desktop';
  });

  // Fonction pour mettre à jour les dimensions
  const updateDimensions = () => {
    screenWidth.value = window.innerWidth;
    screenHeight.value = window.innerHeight;
  };

  // // Écoute des changements de taille d'écran
  // onMounted(() => {
  //   window.addEventListener('resize', updateDimensions);
  //   window.addEventListener('orientationchange', updateDimensions);
  // });

  // onUnmounted(() => {
  //   window.removeEventListener('resize', updateDimensions);
  //   window.removeEventListener('orientationchange', updateDimensions);
  // });

  // Fonction de diagnostic pour débugger les problèmes de détection
  const getDetectionDebugInfo = () => {
    return {
      // Nouvelles propriétés Capacitor
      isNativeApp: isNativeApp.value,
      nativePlatform: nativePlatform.value,
      isNativeIOS: isNativeIOS.value,
      isNativeAndroid: isNativeAndroid.value,
      isMobileWeb: isMobileWeb.value,
      isDesktopWeb: isDesktopWeb.value,
      layoutContext: layoutContext.value,
      // Anciennes propriétés
      ionicPlatforms: getPlatforms(),
      userAgent: navigator.userAgent,
      dimensions: `${screenWidth.value}x${screenHeight.value}`,
      touchSupport: isTouchDevice.value,
      devicePixelRatio: window.devicePixelRatio,
      isRealMobileDevice: isRealMobileDevice.value,
      isRealDesktop: isRealDesktop.value,
      detectedType: deviceType.value,
      allChecks: {
        isMobile: isMobile.value,
        isTablet: isTablet.value,
        isDesktop: isDesktop.value,
        isMobileScreen: isMobileScreen.value,
        isTabletScreen: isTabletScreen.value,
        isDesktopScreen: isDesktopScreen.value,
      },
    };
  };

  return {
    // ============================================
    // NOUVELLES PROPRIÉTÉS (contexte d'utilisation)
    // ============================================

    // Détection Capacitor Native
    isNativeApp: readonly(isNativeApp),
    nativePlatform: readonly(nativePlatform),
    isNativeIOS: readonly(isNativeIOS),
    isNativeAndroid: readonly(isNativeAndroid),

    // Détection contexte Web
    isMobileWeb: readonly(isMobileWeb),
    isDesktopWeb: readonly(isDesktopWeb),

    // Contexte de layout (principal pour adapter l'UI)
    layoutContext: readonly(layoutContext),

    // ============================================
    // ANCIENNES PROPRIÉTÉS (rétrocompatibilité)
    // ============================================

    // Dimensions
    screenWidth: readonly(screenWidth),
    screenHeight: readonly(screenHeight),

    // Détection par plateforme
    isNativeMobile: readonly(isNativeMobile),
    isNativeTablet: readonly(isNativeTablet),
    isNativeDesktop: readonly(isNativeDesktop),

    // Détection par taille d'écran
    isMobileScreen: readonly(isMobileScreen),
    isTabletScreen: readonly(isTabletScreen),
    isDesktopScreen: readonly(isDesktopScreen),

    // Détection avancée
    isRealMobileDevice: readonly(isRealMobileDevice),
    isRealDesktop: readonly(isRealDesktop),

    // Détection combinée (recommandée)
    isMobile: readonly(isMobile),
    isTablet: readonly(isTablet),
    isDesktop: readonly(isDesktop),

    // Orientation
    isPortrait: readonly(isPortrait),
    isLandscape: readonly(isLandscape),

    // Capacités
    isTouchDevice: readonly(isTouchDevice),

    // Type d'appareil
    deviceType: readonly(deviceType),

    // Utilitaires
    updateDimensions,
    getDetectionDebugInfo,
  };
}
